import { getWeek } from 'date-fns'
import type { IIsPeriodInArray, ILeaveFirm, IPublicHolidays } from '../../interface/src/index.ts'

/**
 * Say if the argument is in an array
 * @param array
 * @param id
 * @return boolean
 */
export const isinArray = (array: Array<any>, id: string): boolean => {
  return !!array.find((item) => {
    const isObject = typeof item === 'object'
        && !Array.isArray(item)
        && item !== null
    if (isObject)
      return item._id === id

    return item === id
  })
}

export const fiscalPeriod = ({ date = new Date(), fiscalMonthStart = 0 } = { date: new Date(), fiscalMonthStart: 0 }) => {
  const periodFiscal = fiscalYearStartEnd({ date, fiscalMonthStart })

  const previousDate = structuredClone(date)
  previousDate.setFullYear(previousDate.getFullYear() - 1)
  const nextDate = structuredClone(date)
  nextDate.setFullYear(nextDate.getFullYear() + 1)
  const days = getDaysBetweenDates(periodFiscal.startDate, periodFiscal.endDate)

  return {
    year: periodFiscal.startDate.getFullYear(),
    getPreviousPeriod: () => fiscalPeriod({ date: previousDate, fiscalMonthStart }),
    getNextPeriod: () => fiscalPeriod({ date: nextDate, fiscalMonthStart }),
    display: `${periodFiscal.startDate.toLocaleString('default', { month: 'long', year: 'numeric' })}${periodFiscal.startDate.getFullYear() !== periodFiscal.endDate.getFullYear() ? ` - ${periodFiscal.endDate.toLocaleString('default', { month: 'long', year: 'numeric' })}` : ''}`,
    years: datesToYearAndMonth(days),
  }
}

export const fiscalYearWithMonth = ({ date = new Date(), fiscalMonthStart = 0 }) => {
  const yearCopy = structuredClone(date)
  let year = date.getFullYear()
  if (date.getMonth() < fiscalMonthStart)
    year -= 1

  return {
    year,
    display: `${year}${year !== yearCopy.getFullYear() ? `- ${yearCopy.getFullYear()}` : ''}`,
    months: generateFiscalYearMonthArray({ fiscalMonthStart }),
  }
}

// start and end date of fiscal year
export const fiscalYearStartEnd = ({ date = new Date(), fiscalMonthStart = 0 } = { date: new Date(), fiscalMonthStart: 0 }) => {
  let year = date.getFullYear()
  if (date.getMonth() < fiscalMonthStart)
    year -= 1

  const startDate = new Date(year, fiscalMonthStart, 1)
  const endDate = new Date(year + 1, fiscalMonthStart, 1, 23, 59, 59)
  endDate.setDate(endDate.getDate() - 1) // last day of the previous month

  return {
    startDate,
    endDate,
  }
}

export const generateFiscalYearMonthArray = ({ fiscalMonthStart = 0 } = { fiscalMonthStart: 0 }): Array<number> => {
  const fiscalYearMonthArray: Array<number> = []
  for (let i = 0; i < 12; i++) {
    let month = fiscalMonthStart + i
    if (month > 11)
      month = month - 12

    fiscalYearMonthArray.push(month)
  }
  return fiscalYearMonthArray
}

/**
 * Return the fiscal year of a firm
 * @param date
 * @param fiscalMonthStart
 * @return number
 */
// export function getFiscalYear(date: Date | string, firm: ILeaveFirm): number {
//   let thisYear = new Date(date).getFullYear()
//   if (new Date(date).getMonth() < firm.fiscalMonthStart || 0)
//     thisYear -= 1

//   return thisYear
// };
export function getFiscalYear(date: Date | string, fiscalMonthStart: number): number {
  let thisYear = new Date(date).getFullYear()
  if (new Date(date).getMonth() < fiscalMonthStart || 0)
    thisYear -= 1

  return thisYear
};

/**
 * Return the number of months
 * @param date
 * @param date
 * @return number
 */
export function countMonths(date1: Date | string, date2: Date | string): number {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  d1.setDate(1)
  d1.setDate(d1.getDate() - 1)
  d2.setDate(1)
  d2.setMonth(d2.getMonth() + 1)
  let months
  months = (d2.getFullYear() - d1.getFullYear()) * 12
  months -= d1.getMonth() + 1
  months += d2.getMonth()
  return months <= 0 ? 0 : months
};

/**
 * Return if the period is contain in the array of periods
 * @param array
 * @param period
 * @return boolean
 */
export function isPeriodInArray(array: Array<IIsPeriodInArray>, period: Array<number>): boolean {
  for (let i = 0; i < array.length; i++) {
    if (array[i].period[0] == period[0] && array[i].period[1] == period[1] && array[i].period[2] == period[2])
      return true
  }
  return false
}

/**
 * Get if the date is less or equal to an other date
 * @param firstDate
 * @param secondDate
 * @returns boolean
 */
export function isDateLessOrEqualDate(firstDate: Date | string, secondDate: Date | string): boolean {
  const date1 = new Date(firstDate)
  date1.setHours(0, 0, 0, 0)
  const date2 = new Date(secondDate)
  date2.setHours(0, 0, 0, 0)

  return (date1 <= date2)
}

/**
 * Get if the date is less or equal to an other date
 * @param firstDate
 * @param secondDate
 * @returns boolean
 */
export function isDateLessDate(firstDate: Date | string, secondDate: Date | string): boolean {
  const date1 = new Date(firstDate)
  date1.setHours(0, 0, 0, 0)
  const date2 = new Date(secondDate)
  date2.setHours(0, 0, 0, 0)

  return (date1 < date2)
}


/**
 * Get if the date is between two other dates
 * @param date
 * @param date1
 * @param date2
 * @returns boolean
 */
export function isDateBetweenDates(date: Date | string, date1: Date | string, date2: Date | string): boolean {
  return isDateLessOrEqualDate(date1, date) && isDateLessOrEqualDate(date, date2)
}

/**
 * Get if the key is in the array
 * @param array
 * @param element
 * @param key
 * @returns boolean
 */
export function isKeyElementinArray<T, K extends keyof T>(array: T[], element: any, key: K): boolean {
  for (let i = 0; i < array.length; i++) {
    if (array[i][key] !== null) {
      if (array[i][key] === element)
        return true
    }
  }
  return false
}

export function isDateInArrayKey(array: Array<any>, thisDate: string | Date, key: string) {
  for (let i = 0; i < array.length; i++) {
    if (isDateEqual(array[i][key], thisDate))
      return true
  }
  return false
}

export function isDateEqual(firstDate: string | Date, secondDate: string | Date) {
  const date1 = new Date(firstDate)
  date1.setHours(0, 0, 0, 0)
  const date2 = new Date(secondDate)
  date2.setHours(0, 0, 0, 0)
  return (date1.getDate() == date2.getDate()
        && date1.getMonth() == date2.getMonth()
        && date1.getFullYear() == date2.getFullYear())
}

export const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))

export type TParsedEmailReplyContent = {
  currentMessage: string
  quotedContent: string
  hasQuotedContent: boolean
}

const unwrapSingleOuterContainer = (content = '') => {
  const trimmedContent = content.trim()
  const outerWrapperMatch = trimmedContent.match(/^<(div|p|span|font)\b[^>]*>([\s\S]*)<\/\1>$/i)

  if (!outerWrapperMatch)
    return trimmedContent

  return outerWrapperMatch[2].trim()
}

export const splitEmailReplyContent = (content = ''): TParsedEmailReplyContent => {
  const normalizedContent = unwrapSingleOuterContainer(content)

  if (!normalizedContent) {
    return {
      currentMessage: '',
      quotedContent: '',
      hasQuotedContent: false,
    }
  }

  const quoteMarkers = [
    { regex: /<div[^>]*class=["'][^"']*gmail_quote_container[^"']*["'][^>]*>/i, allowShortReply: true },
    { regex: /<div[^>]*class=["'][^"']*gmail_quote[^"']*["'][^>]*>/i, allowShortReply: true },
    { regex: /<blockquote[^>]*class=["'][^"']*bc-quoted-content[^"']*["'][^>]*>/i, allowShortReply: true },
    { regex: /<blockquote[^>]*class=["'][^"']*gmail_quote[^"']*["'][^>]*>/i, allowShortReply: true },
    { regex: /<blockquote\b[^>]*>/i, allowShortReply: true },
    { regex: /-{6,}/i, allowShortReply: false },
    { regex: /On\s+.+?wrote:/i, allowShortReply: true },
    { regex: /Le\s+.+?a écrit\s*:/i, allowShortReply: true },
  ]

  let splitIndex = -1
  let matchedMarkerAllowsShortReply = false
  for (const marker of quoteMarkers) {
    const match = marker.regex.exec(normalizedContent)
    if (!match || match.index === undefined) continue

    if (splitIndex === -1 || match.index < splitIndex) {
      splitIndex = match.index
      matchedMarkerAllowsShortReply = marker.allowShortReply
    }
  }

  if (splitIndex === -1) {
    return {
      currentMessage: normalizedContent.trim(),
      quotedContent: '',
      hasQuotedContent: false,
    }
  }

  const currentMessage = normalizedContent.slice(0, splitIndex).trim()
  const quotedContent = normalizedContent.slice(splitIndex).trim()
  const plainCurrentMessage = currentMessage
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  if (plainCurrentMessage.length < 20 && !matchedMarkerAllowsShortReply) {
    return {
      currentMessage: normalizedContent.trim(),
      quotedContent: '',
      hasQuotedContent: false,
    }
  }

  return {
    currentMessage,
    quotedContent,
    hasQuotedContent: !!quotedContent,
  }
}

export const datesAreOnSameDay = (first: Date, second: Date) => new Date(first).getFullYear() === new Date(second).getFullYear()
    && new Date(first).getMonth() === new Date(second).getMonth()
    && new Date(first).getDate() === new Date(second).getDate()

export const datesAreOnSameMonth = (first: Date, second: Date) => new Date(first).getFullYear() === new Date(second).getFullYear()
    && new Date(first).getMonth() === new Date(second).getMonth()

export const datesAreDaysApart = (first: Date, second: Date, nbDays = 1) => Math.abs(new Date(first).getTime() - new Date(second).getTime()) <= (86400000*nbDays)

export const isWeekend = function (day: Date) {
  return day.getDay() == 0 || day.getDay() == 6
}

type TYearAndMonth = { year: number; months: { month: number; dates: Date[]; display: string }[] }[]
/** `display` labels each month ("octobre 2026") in `locale`, the browser's language when omitted. */
export const datesToYearAndMonth = (dates: Date[], locale?: string) => {
  return dates.reduce((acc: TYearAndMonth, date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const foundYear = acc.findIndex(item => item.year === year)
    if (foundYear > -1) {
      const foundMonth = acc[foundYear].months.findIndex(item => item.month === month)
      if (foundMonth > -1) {
        acc[foundYear].months[foundMonth].dates.push(date)
      }
      else {
        acc[foundYear].months.push({
          month,
          display: date.toLocaleString(locale, { month: 'long', year: 'numeric' }),
          dates: [date],
        })
      }
      return acc
    }
    acc.push({
      year,
      months: [{
        month,
        display: new Date(year, month).toLocaleString(locale, { month: 'long', year: 'numeric' }),
        dates: [date],
      }],
    })
    return acc
  }, [])
}

type TYearAndMonthAndWeek = { year: number; months: { month: number; weeks: { weekNumber: number; dates: Date[] }[]; display: string }[] }[]
export const datesToYearAndMonthAndWeek = (dates: Date[], locale?: string) => {
  return dates.reduce((acc: TYearAndMonthAndWeek, date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const weekNumber = getWeek(date, { weekStartsOn: 1 })
    const foundYear = acc.findIndex(item => item.year === year)
    if (foundYear > -1) {
      const foundMonth = acc[foundYear].months.findIndex(item => item.month === month)
      if (foundMonth > -1) {
        const foundWeek = acc[foundYear].months[foundMonth].weeks.findIndex(item => item.weekNumber === weekNumber)
        if (foundWeek > -1) { acc[foundYear].months[foundMonth].weeks[foundWeek].dates.push(date) }
        else {
          acc[foundYear].months[foundMonth].weeks.push({
            weekNumber,
            dates: [date],
          })
        }
      }
      else {
        acc[foundYear].months.push({
          month,
          display: date.toLocaleString(locale, { month: 'long', year: 'numeric' }),
          weeks: [{
            weekNumber,
            dates: [date],
          }],
        })
      }
      return acc
    }
    acc.push({
      year,
      months: [{
        month,
        display: new Date(year, month).toLocaleString(locale, { month: 'long', year: 'numeric' }),
        weeks: [{
          weekNumber,
          dates: [date],
        }],
      }],
    })
    return acc
  }, [])
}

export const datesToWeeks = (dates: Date[]) => {
  return dates.reduce((acc: { weekNumber: number; dates: Date[] }[], date) => {
    const weekNumber = getWeek(date, { weekStartsOn: 1 })
    const foundWeek = acc.findIndex(item => item.weekNumber === weekNumber)
    if (foundWeek > -1) { acc[foundWeek].dates.push(date) }
    else {
      acc.push({
        weekNumber,
        dates: [date],
      })
    }
    return acc
  }, [])
}

export const arrayOfMonthsBetweenTwoDates = (startDate: Date, endDate: Date) => {
  const dates = getDaysBetweenDates(startDate, endDate)
  return dates.reduce<{ year: number; month: number }[]>((acc, currentValue) => {
    const month = currentValue.getMonth()
    const year = currentValue.getFullYear()
    const foundMonth = acc.find(item => item.year === year && item.month === month)
    if (!foundMonth)
      acc.push({ year, month })
    return acc
  }, [])
}

const MS_PER_DAY = 24 * 3600 * 1000;

/**
 * Calculates the start of the day (00:00:00 local time) for a given Date object,
 * and returns its UTC millisecond timestamp.
 * This is important for comparing dates by their calendar day in local time,
 * irrespective of their time components or UTC offsets.
 */
const getUTCMillisecondsAtStartOfLocalDay = (date: Date): number => {
  // Create a new Date object that represents 00:00:00 in the *local* timezone
  // for the given date's year, month, and day.
  const startOfLocalDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // Return the UTC milliseconds for this local start-of-day.
  return startOfLocalDay.getTime();
};

export const numberOfDaysBetweenTwoDates = (date1: Date, date2: Date): number => {
  // Get the UTC millisecond timestamp for the start of the local day for date1
  const startMs1 = getUTCMillisecondsAtStartOfLocalDay(date1);
  // Get the UTC millisecond timestamp for the start of the local day for date2
  const startMs2 = getUTCMillisecondsAtStartOfLocalDay(date2);

  // Compute the difference directly between the two local-midnight timestamps.
  // Math.round absorbs the ±1 hour DST shift so that a range spanning a DST
  // boundary (e.g. UK BST→GMT) still counts the correct number of calendar days.
  // Without Math.round, flooring each timestamp's UTC day number separately
  // causes an off-by-one when start is in UTC+1 (local midnight = 23:00 UTC,
  // day N-1) and end is in UTC+0 (local midnight = 00:00 UTC, day N+30),
  // yielding 32 instead of 31 days for a 31-day month like October.
  const diffInDayNumbers = Math.round(Math.abs(startMs2 - startMs1) / MS_PER_DAY);

  // Add 1 to make the count inclusive.
  // - If same local day (e.g., Oct 26th to Oct 26th), diffInDayNumbers is 0. Result: 0 + 1 = 1 day.
  // - If consecutive local days (e.g., Oct 26th to Oct 27th), diffInDayNumbers is 1. Result: 1 + 1 = 2 days.
  return diffInDayNumbers + 1;
};

export const getDaysBetweenDates = (startDate: Date, endDate: Date) => {
  const planningDays = []
  const numberOfDays = numberOfDaysBetweenTwoDates(startDate, endDate)

  for (let i = 0; i < numberOfDays; i++) { // Loop on all days in the period given
    const dateTemp = new Date(startDate)
    dateTemp.setDate(dateTemp.getDate() + i) // add i days to the starting date
    planningDays.push(dateTemp) // push new date in planningDays
  }
  return planningDays
}

export const getNumberOfWeeksBetweenTwoDates = (date1: Date, date2: Date) => {
  const numberOfDays = numberOfDaysBetweenTwoDates(date1, date2)
  return Math.ceil(numberOfDays / 7)
}

export const getNumberOfWorkingDayBetweenTwoDates = (date1: Date, date2: Date) => {
  const numberOfDays = numberOfDaysBetweenTwoDates(date1, date2)
  let numberOfWorkingDays = 0
  for (let i = 0; i < numberOfDays; i++) {
    const dateTemp = new Date(date1)
    dateTemp.setDate(dateTemp.getDate() + i)
    if (!isWeekend(dateTemp))
      numberOfWorkingDays++
  }
  return numberOfWorkingDays
}

export const getPreviousMonday = () => {
  const date = new Date()
  const day = date.getDay()
  const prevMonday = new Date()
  if (day == 0 || day == 1)
    prevMonday.setDate(date.getDate() - (day + 6))

  else
    prevMonday.setDate(date.getDate() - (day - 1))

  return prevMonday
}

export const roundNumber = (numberToRound: number, options = { decimal: 2 }) => {
  const places = 10 ** options.decimal
  const res = Math.round(numberToRound * places) / places
  return (res)
}

/**
 *
 * Functions get text color from background hexa color
 *
 */

export {
  adaptHtmlColorsForContrast,
  ensureReadableTextColor,
  extractCssColorToken,
  getContrast,
  getLuminance,
  getRGB,
  getsRGB,
  getTextColor,
  isNeutralCssColor,
  normalizeHexColor,
  parseCssColorToHex,
} from './colorContrast.ts'

export { prepareEditorHtmlForEmail, wrapEmailHtmlForIsolatedPreview } from './prepareEditorHtmlForEmail.ts'
export { getTimeZoneOffsetMinutes } from './timeZoneOffset.ts'
export { getDisplayLocale } from './displayLocale.ts'
export { PROFILE_LEVELS, PROFILE_RIGHT_LEVELS, computeProfileLevel, getProfileRightLevels } from './profileLevel.ts'
export type { TProfileLevel } from './profileLevel.ts'

// positive magniture lighten the hex color and negative darken the color
export const newShade = (hexColor: string, magnitude: number) => {
  hexColor = hexColor.replace('#', '')
  if (hexColor.length === 6) {
    const decimalColor = Number.parseInt(hexColor, 16)
    let r = (decimalColor >> 16) + magnitude
    r > 255 && (r = 255)
    r < 0 && (r = 0)
    let g = (decimalColor & 0x0000FF) + magnitude
    g > 255 && (g = 255)
    g < 0 && (g = 0)
    let b = ((decimalColor >> 8) & 0x00FF) + magnitude
    b > 255 && (b = 255)
    b < 0 && (b = 0)
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`
  }
  else {
    return hexColor
  }
}

export const newDateToUTC = (stringDate: string | Date, tzDate: string | Date = stringDate) => {
  const thisDate = new Date(stringDate)
  thisDate.setMinutes(thisDate.getMinutes() - (tzDate ? new Date(tzDate).getTimezoneOffset() : new Date().getTimezoneOffset()))
  return thisDate
}

export const newDateFromUTC = (stringDate: string | Date, tzDate: string | Date = stringDate) => { // Transfor UTC to local date & time
  const thisDate = new Date(stringDate)
  thisDate.setMinutes(thisDate.getMinutes() + (tzDate ? new Date(tzDate).getTimezoneOffset() : new Date().getTimezoneOffset()))
  return thisDate
}

export const getAgeFromBirthdate = (birthdate: Date | string, referenceDate = new Date()) => {
  const birth = birthdate instanceof Date ? birthdate : new Date(birthdate)
  if (Number.isNaN(birth.getTime())) return null

  let age = referenceDate.getFullYear() - birth.getFullYear()
  const monthDiff = referenceDate.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birth.getDate()))
    age -= 1

  return age >= 0 ? age : null
}
export const dateToUTC = (currentDate: Date, timezoneOffset = currentDate.getTimezoneOffset()) => {
  const copyDate = new Date(currentDate)
  copyDate.setMinutes(currentDate.getMinutes() - timezoneOffset)
  return copyDate
}

export const dateFromUTC = (currentDate: Date, timezoneOffset = currentDate.getTimezoneOffset()) => { // Transfor UTC to local date & time
  const copyDate = new Date(currentDate)
  copyDate.setMinutes(currentDate.getMinutes() + timezoneOffset)
  return copyDate
}

export const findFirstInArrayOfDates = (dates: { startDate: Date }[]) => {
  return dates.reduce((acc: Date | null, date) => {
    if (acc && acc < date.startDate)
      return acc
    return date.startDate
  }, null)
}

export const findLastInArrayOfDates = (dates: { startDate: Date }[]) => {
  return dates.reduce((acc: Date | null, date) => {
    if (acc && acc > date.startDate)
      return acc
    return date.startDate
  }, null)
}

export const isValidEmail = (email: string) => {
  return /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(email)
}

export const workingDaysBetweenDates = (start:Date, end:Date) => {
  var startDate = new Date(start);
  var endDate = new Date(end);
  startDate.setHours(0,0,0,0);
  endDate.setHours(0,0,0,0);
  endDate.setDate(endDate.getDate()+1);
  var days = 0;
  var thisDate = new Date(startDate);
  var i = 0;
  while (thisDate.getTime() < endDate.getTime() && i < 10000){
      if(thisDate.getDay()!=0 && thisDate.getDay()!=6){
          days++;
      }
      thisDate.setDate(thisDate.getDate()+1);
      i++
  }
  return days * 1;
}

export const holidaysBetweenDates = (start:Date, end:Date, holidays:Date[]) => {
  var count = 0;
  holidays.forEach(function(holiday){
      if (isDateBetweenDates(holiday, start, end) && !isWeekend(holiday)) {
          count++;
      };
  })
  return count;
}

export const formatDaysToYearsMonthsAndDays = (daysDuration:number) => {
  let years = Math.floor(daysDuration / 365)
  let months = Math.floor((daysDuration % 365) / 30)
  const days = Math.floor((daysDuration % 365) % 30)
  if (months >= 12) {
    years += 1
    months -= 12
  }
  return {
    years,
    months,
    days,
  }
}

export const formatDaysToMonths = (daysDuration:number) => {
  const months = Math.floor(daysDuration / 30)
  return {
    months,
  }
}

